import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { formatUsPhone } from '../../../utilities/formatPhone';
import '../../../styles/viewAdditionalInterest.css';

export type AIPolicy = {
  databaseId?: string;
  number?: string;
  effectiveDate?: string | null;
  expirationDate?: string | null;
  linesOfBusiness?: string[];
  carrierName?: string | null;
  customFields?: string | null;
};

export type AdditionalInterestDetail = {
  databaseId?: string;
  certificateHolderName?: string;
  email?: string | null;
  cellPhone?: string;
  phone?: string;
  fax?: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  zipCode?: string | null;
  stateAbbreviationCode?: string | null;
  additionalInsured?: boolean;
  lossPayee?: boolean;
  breachOfWarranty?: boolean;
  mortgagee?: boolean;
  coOwner?: boolean;
  owner?: boolean;
  leasebackOwner?: boolean;
  registrant?: boolean;
  lienholder?: boolean;
  trustee?: boolean;
  other?: boolean;
  certificateHolder?: boolean;
  lessor?: boolean;
  registeredOwner?: boolean;
  additionalInterestOtherText?: string;
  loanNumber?: string;
  description?: string;
  generalLiability?: boolean;
  automobileLiability?: boolean;
  umbrellaLiability?: boolean;
  garageLiability?: boolean;
  garageKeepersLiability?: boolean;
  cargo?: boolean;
  trailerInterchange?: boolean;
  otherLiability?: boolean;
  otherLiability2?: boolean;
  generalLiabilitySubrWvd?: boolean;
  automobileLiabilitySubrWvd?: boolean;
  umbrellaLiabilitySubrWvd?: boolean;
  garageLiabilitySubrWvd?: boolean;
  garageKeepersLiabilitySubrWvd?: boolean;
  cargoSubrWvd?: boolean;
  trailerInterchangeSubrWvd?: boolean;
  otherLiabilitySubrWvd?: boolean;
  otherLiability2SubrWvd?: boolean;
  accord101?: boolean;
  active?: boolean;
  deactiveDate?: string | null;
  policies?: AIPolicy[];
  professionalLiabilities?: { firstName?: string; lastName?: string }[];
  insuredDatabaseId?: string;
  insuredEmail?: string;
  insuredFirstName?: string;
  insuredLastName?: string;
  insuredCommercialName?: string;
  createDate?: string | null;
  changeDate?: string | null;
  customFields?: string | null;
};

export type ViewAdditionalInterestProps = {
  visible: boolean;
  onHide: () => void;
  interest: AdditionalInterestDetail | null;
};

const INTEREST_TYPES: { key: keyof AdditionalInterestDetail; label: string; color: string }[] = [
  { key: 'additionalInsured', label: 'Additional Insured', color: '#0057A4' },
  { key: 'lossPayee', label: 'Loss Payee', color: '#059669' },
  { key: 'breachOfWarranty', label: 'Breach of Warranty', color: '#f59e42' },
  { key: 'mortgagee', label: 'Mortgagee', color: '#a21caf' },
  { key: 'coOwner', label: 'Co-Owner', color: '#0891b2' },
  { key: 'owner', label: 'Owner', color: '#f43f5e' },
  { key: 'leasebackOwner', label: 'Leaseback Owner', color: '#eab308' },
  { key: 'registrant', label: 'Registrant', color: '#6366f1' },
  { key: 'lienholder', label: 'Lienholder', color: '#10b981' },
  { key: 'trustee', label: 'Trustee', color: '#f472b6' },
  { key: 'certificateHolder', label: 'Certificate Holder', color: '#0891b2' },
  { key: 'lessor', label: 'Lessor', color: '#7c3aed' },
  { key: 'registeredOwner', label: 'Registered Owner', color: '#ea580c' },
  { key: 'other', label: 'Other', color: '#64748b' },
];

const COVERAGE_LINES: { key: keyof AdditionalInterestDetail; subrKey: keyof AdditionalInterestDetail; label: string }[] = [
  { key: 'generalLiability', subrKey: 'generalLiabilitySubrWvd', label: 'General Liability' },
  { key: 'automobileLiability', subrKey: 'automobileLiabilitySubrWvd', label: 'Automobile Liability' },
  { key: 'umbrellaLiability', subrKey: 'umbrellaLiabilitySubrWvd', label: 'Umbrella Liability' },
  { key: 'garageLiability', subrKey: 'garageLiabilitySubrWvd', label: 'Garage Liability' },
  { key: 'garageKeepersLiability', subrKey: 'garageKeepersLiabilitySubrWvd', label: 'Garage Keepers Liability' },
  { key: 'cargo', subrKey: 'cargoSubrWvd', label: 'Cargo' },
  { key: 'trailerInterchange', subrKey: 'trailerInterchangeSubrWvd', label: 'Trailer Interchange' },
  { key: 'otherLiability', subrKey: 'otherLiabilitySubrWvd', label: 'Other Liability' },
  { key: 'otherLiability2', subrKey: 'otherLiability2SubrWvd', label: 'Other Liability 2' },
];

function formatDate(value?: string | null): string {
  if (!value) return '-';
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = iso ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])) : new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getPolicyTypeLabel(p: AIPolicy, interestCustomFields?: string | null): string {
  if (interestCustomFields) {
    try {
      const fields: { Key: string; Value: string }[] = JSON.parse(interestCustomFields);
      const policyType = fields.find((f) => f.Key === 'PolicyType')?.Value;
      if (policyType) return policyType;
    } catch {
      // fall through to linesOfBusiness
    }
  }
  return Array.isArray(p.linesOfBusiness) && p.linesOfBusiness.length > 0
    ? p.linesOfBusiness.join(', ')
    : '-';
}

function buildAddress(ai: AdditionalInterestDetail): string {
  const parts: string[] = [];
  if (ai.addressLine1) parts.push(ai.addressLine1);
  if (ai.addressLine2) parts.push(ai.addressLine2);
  const cityState: string[] = [];
  if (ai.city) cityState.push(ai.city);
  if (ai.stateAbbreviationCode) cityState.push(ai.stateAbbreviationCode);
  if (cityState.length > 0) {
    let cs = cityState.join(', ');
    if (ai.zipCode) cs += ' ' + ai.zipCode;
    parts.push(cs);
  }
  return parts.length > 0 ? parts.join(', ') : '-';
}

const ViewAdditionalInterest: React.FC<ViewAdditionalInterestProps> = ({ visible, onHide, interest }) => {
  if (!interest) return null;

  const activeBadges = INTEREST_TYPES.filter((t) => interest[t.key]);
  const activeCoverages = COVERAGE_LINES.filter((c) => interest[c.key]);

  const header = (
    <div className="vai__dialogHeader">
      <i className="pi pi-users vai__dialogHeaderIcon" />
      <div>
        <div className="vai__dialogTitle">Additional Interest Details</div>
        <div className="vai__dialogSubtitle">{interest.certificateHolderName || '-'}</div>
      </div>
    </div>
  );

  return (
    <Dialog
      header={header}
      visible={visible}
      onHide={onHide}
      className="vai__dialog"
      modal
      draggable={false}
      resizable={false}
    >
      <div className="vai__body">
        {/* Status + Type badges */}
        <div className="vai__statusRow">
          <Tag
            value={interest.active ? 'Active' : 'Inactive'}
            severity={interest.active ? 'success' : 'danger'}
          />
          {interest.accord101 && <Tag value="Accord 101" severity="info" />}
        </div>

        {/* Interest Types */}
        {activeBadges.length > 0 && (
          <div className="vai__badgeRow">
            {activeBadges.map((b) => (
              <span
                key={b.key}
                className="vai__badge"
                style={{ background: b.color + '18', color: b.color, border: `1px solid ${b.color}` }}
              >
                {b.key === 'other' && interest.additionalInterestOtherText
                  ? interest.additionalInterestOtherText
                  : b.label}
              </span>
            ))}
          </div>
        )}

        {/* Contact Information */}
        <div className="vai__section">
          <div className="vai__sectionLabel">
            <i className="pi pi-id-card" />
            Contact Information
          </div>
          <div className="vai__grid">
            <div className="vai__field">
              <div className="vai__fieldLabel">Name</div>
              <div className="vai__fieldValue vai__fieldValue--bold">{interest.certificateHolderName || '-'}</div>
            </div>
            <div className="vai__field">
              <div className="vai__fieldLabel">Email</div>
              <div className="vai__fieldValue">{interest.email || '-'}</div>
            </div>
            <div className="vai__field">
              <div className="vai__fieldLabel">Phone</div>
              <div className="vai__fieldValue">{formatUsPhone(interest.phone) || '-'}</div>
            </div>
            <div className="vai__field">
              <div className="vai__fieldLabel">Cell Phone</div>
              <div className="vai__fieldValue">{formatUsPhone(interest.cellPhone) || '-'}</div>
            </div>
            <div className="vai__field">
              <div className="vai__fieldLabel">Fax</div>
              <div className="vai__fieldValue">{formatUsPhone(interest.fax) || '-'}</div>
            </div>
            <div className="vai__field">
              <div className="vai__fieldLabel">Address</div>
              <div className="vai__fieldValue">{buildAddress(interest)}</div>
            </div>
          </div>
        </div>


        {/* Coverage Lines */}
        {/* {activeCoverages.length > 0 && (
          <div className="vai__section">
            <div className="vai__sectionLabel">
              <i className="pi pi-shield" />
              Coverage Lines
            </div>
            <div className="vai__coverageList">
              {activeCoverages.map((c) => (
                <div key={c.key} className="vai__coverageItem">
                  <span className="vai__coverageName">{c.label}</span>
                  {interest[c.subrKey] && (
                    <span className="vai__coverageSubr">Subr Wvd</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )} */}

        {/* Loan Number */}
        {interest.loanNumber && (
          <div className="vai__section">
            <div className="vai__sectionLabel">
              <i className="pi pi-info-circle" />
              Other Details
            </div>
            <div className="vai__grid">
              <div className="vai__field">
                <div className="vai__fieldLabel">Loan Number</div>
                <div className="vai__fieldValue vai__fieldValue--mono">{interest.loanNumber}</div>
              </div>
            </div>
          </div>
        )}

        {/* Linked Policies */}
        {interest.policies && interest.policies.length > 0 && (
          <div className="vai__section">
            <div className="vai__sectionLabel">
              <i className="pi pi-file" />
              Linked Policies ({interest.policies.length})
            </div>
            <div className="vai__policiesList">
              {interest.policies.map((p, idx) => (
                <div key={p.databaseId || idx} className="vai__policyCard">
                  <div className="vai__policyTop">
                    <span className="vai__policyNumber">{p.number || '-'}</span>
                    <span className="vai__policyCarrier">{p.carrierName || ''}</span>
                  </div>
                  <div className="vai__policyMeta">
                    {getPolicyTypeLabel(p, interest.customFields)}
                  </div>
                  <div className="vai__policyDates">
                    {formatDate(p.effectiveDate)} - {formatDate(p.expirationDate)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Professional Liabilities */}
        {interest.professionalLiabilities && interest.professionalLiabilities.length > 0 && (
          <div className="vai__section vai__section--last">
            <div className="vai__sectionLabel">
              <i className="pi pi-briefcase" />
              Professional Liabilities
            </div>
            <div className="vai__grid">
              {interest.professionalLiabilities.map((pl, idx) => (
                <div key={idx} className="vai__field">
                  <div className="vai__fieldValue">
                    {[pl.firstName, pl.lastName].filter(Boolean).join(' ') || '-'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div className="vai__timestamps">
          <span>Created: {formatDate(interest.createDate)}</span>
          <span>Last Modified: {formatDate(interest.changeDate)}</span>
        </div>
      </div>
    </Dialog>
  );
};

export default ViewAdditionalInterest;
